import { createContext, useContext } from 'react';
import { GOOGLE_FONT_WEIGHTS, designFonts, googleFontsHref, type Branding, type FontRef, type FontRole, type PublicAsset, type SiteDesign, type UiScale } from '@bulk/validation';
import { schemeColors } from './schemes';
import './site.css';

const SiteDesignContext = createContext<SiteDesign | null>(null);

export const SiteDesignProvider = SiteDesignContext.Provider;

export function useSiteDesign(): SiteDesign | null {
  return useContext(SiteDesignContext);
}

const SYSTEM_STACKS: Record<Extract<FontRef, { source: 'system' }>['family'], string> = {
  sans: 'var(--font-sans)',
  serif: "Georgia, 'Noto Serif Georgian', 'Times New Roman', serif",
  condensed: "'Arial Narrow', 'Roboto Condensed', 'Noto Sans Georgian', sans-serif",
  mono: "ui-monospace, SFMono-Regular, Menlo, 'Noto Sans Georgian', monospace",
};

const customFamily = (font: Extract<FontRef, { source: 'custom' }>) => `f-${font.regularAssetId}`;

function familyOf(font: FontRef): string {
  switch (font.source) {
    case 'system':
      return SYSTEM_STACKS[font.family];
    case 'google':
      return `'${font.family}'`;
    case 'custom':
      return `'${customFamily(font)}'`;
  }
}

/** Latin face first and the Georgian one after it, so the browser takes each letter from the first face that has it. */
export function fontStack(role: FontRole | null): string | null {
  if (!role) return null;
  const faces = [familyOf(role.latin), ...(role.georgian ? [familyOf(role.georgian)] : [])];
  return [...faces, 'var(--font-sans)'].join(', ');
}

/**
 * A face covers the whole weight range it is used for, so the browser draws a heavy heading with the
 * file it has instead of smearing a fake bold over a display face that was never meant to carry one.
 */
function fontFaces(design: SiteDesign, assets: Record<string, PublicAsset>): string {
  const seen = new Set<string>();
  return designFonts(design)
    .flatMap((font) => {
      if (font.source !== 'custom' || seen.has(font.regularAssetId)) return [];
      seen.add(font.regularAssetId);
      const faces = [{ id: font.regularAssetId, weight: font.boldAssetId ? '100 599' : '100 900' }, ...(font.boldAssetId ? [{ id: font.boldAssetId, weight: '600 900' }] : [])];
      return faces.flatMap(({ id, weight }) => {
        const asset = assets[id];
        return asset ? [`@font-face { font-family: '${customFamily(font)}'; src: url(${JSON.stringify(asset.url)}); font-weight: ${weight}; font-display: swap; }`] : [];
      });
    })
    .join('\n');
}

type Radius = NonNullable<SiteDesign['shape']['radius']>;

const RADII: Record<Radius, Record<string, string>> = {
  none: { xs: '0', sm: '0', md: '0', lg: '0', xl: '0', '2xl': '0', '3xl': '0', card: '0' },
  sm: { sm: '0.125rem', md: '0.25rem', lg: '0.25rem', xl: '0.375rem', '2xl': '0.5rem', '3xl': '0.75rem', card: '0.375rem' },
  md: { card: '1rem' },
  lg: { md: '0.5rem', lg: '0.75rem', xl: '1rem', '2xl': '1.25rem', '3xl': '2rem', card: '1.5rem' },
  full: { md: '0.75rem', lg: '1rem', xl: '1.5rem', '2xl': '2rem', '3xl': '2.5rem', card: '2rem' },
};

function rule(selector: string, declarations: (string | false | null)[]): string {
  const body = declarations.filter((d): d is string => typeof d === 'string');
  return body.length > 0 ? `${selector} { ${body.join('; ')}; }` : '';
}

const UI_TEXT_SIZES = { xs: 0.75, sm: 0.875, base: 1, lg: 1.125, xl: 1.25 } as const;

/**
 * Multiplies every Tailwind text size on what `selector` picks by `--site-ui-scale`, by redefining the size
 * variables the `text-*` utilities read, so no component changes. Nothing at all is drawn for the default 1.
 */
export function uiScaleCss(selector: string, scale: UiScale): string {
  if (scale === 1) return '';
  return rule(
    selector,
    Object.entries(UI_TEXT_SIZES).map(([name, rem]) => `--text-${name}: calc(${rem}rem * var(--site-ui-scale))`),
  );
}

/** A bold exists for a system face, a Google family that offers 700, an uploaded face with a bold file, and for the site's own sans when no UI face is set. */
function hasBold(ui: FontRole | null): boolean {
  const latin = ui?.latin;
  if (!latin || latin.source === 'system') return true;
  if (latin.source === 'google') return GOOGLE_FONT_WEIGHTS[latin.family].includes(700);
  return latin.boldAssetId !== null;
}

/**
 * Draws what `selector` picks heavier when the design asks for bold UI text: weight 700 for a face that has one, else a thin
 * stroke in the letters' own colour under their fill. The stroke is for Latin letters only (`:lang(en)`), since the same stroke
 * fills in the counters of Georgian ones.
 */
export function uiBoldCss(selector: string, type: SiteDesign['typography']): string {
  if (!type.uiBold) return '';
  if (hasBold(type.ui)) return rule(selector, ['font-weight: 700']);
  return rule(`${selector}:lang(en)`, ['-webkit-text-stroke: 0.03em currentColor', 'paint-order: stroke fill']);
}

function designRules(design: SiteDesign, assets: Record<string, PublicAsset>): string[] {
  const { typography: type, shape } = design;
  const heading = fontStack(type.heading);
  const body = fontStack(type.body);
  const ui = fontStack(type.ui);
  const radii = shape.radius ? RADII[shape.radius] : {};
  const button = shape.button;

  return [
    fontFaces(design, assets),
    rule('.site', [
      ...Object.entries(radii).map(([key, value]) => (key === 'card' ? `--radius-card: ${value}` : `--radius-${key}: ${value}`)),
      body && `font-family: ${body}`,
      heading && `--site-font-heading: ${heading}`,
      ui && `--site-font-ui: ${ui}`,
      type.uiScale !== 1 && `--site-ui-scale: ${type.uiScale}`,
    ]),
    rule('.site :is(h1, h2, h3, h4)', [
      heading && `font-family: ${heading}`,
      type.headingCase && `text-transform: ${type.headingCase}`,
      type.headingWeight !== null && `font-weight: ${type.headingWeight}`,
      type.headingTracking !== null && `letter-spacing: ${type.headingTracking}em`,
    ]),
    rule('.site :is(.site-ui, .site-eyebrow)', [ui && `font-family: ${ui}`]),
    rule('.site .site-ui', [type.uiCase && `text-transform: ${type.uiCase}`, type.uiTracking !== null && `letter-spacing: ${type.uiTracking}em`]),
    uiScaleCss('.site :is(.site-ui, .site-eyebrow)', type.uiScale),
    type.uiScale !== 1 ? rule('.site .site-btn', ['min-height: calc(var(--site-btn-h, 0px) * var(--site-ui-scale))']) : '',
    button === 'pill' ? rule('.site .site-btn', ['border-radius: 999px']) : '',
    button === 'block' ? rule('.site .site-btn', ['border-radius: 0', 'padding-inline: 1.75rem', 'font-weight: 600']) : '',
    button === 'outline' ? rule('.site .site-btn-primary', ['background: transparent', 'color: var(--brand-primary)', 'box-shadow: inset 0 0 0 2px var(--brand-primary)']) : '',
    button === 'outline' ? rule('.site .site-btn-primary:hover', ['background: var(--brand-primary)', 'color: var(--brand-primary-foreground)']) : '',
    uiBoldCss('.site :is(.site-ui, .site-eyebrow)', type),
  ];
}

const customRules = (design: SiteDesign): string => (design.customCss.trim() ? `@scope (.site) {\n${design.customCss}\n}` : '');

/**
 * The rules a design adds to the site. They are unlayered, so they win over the Tailwind utilities
 * the blocks are drawn with; that is the point, because a design restyles every block at once. The
 * corner radii work the same way, by redefining the variables Tailwind's `rounded-*` classes read.
 * Each rule is written only for what the design sets, and everything is scoped to `.site`, so the
 * same sheet can sit inside the studio's preview without touching the console around it.
 */
export function designCss(design: SiteDesign | null, assets: Record<string, PublicAsset>): string {
  if (!design) return '';
  return [...designRules(design, assets), customRules(design)].filter(Boolean).join('\n');
}

export interface ControlsPalette {
  background: string;
  text: string;
  accent: string;
}

export interface ControlsScope {
  /** The selector the controls are drawn under: `.site` here, the member area's and the sign-in pages' own roots when they take them over. */
  scope: string;
  /** The default scheme's colours as hex, for the one rule that sits outside the scope: the page's own scrollbar. */
  palette: ControlsPalette;
  /** `portal` is Bulk's own screens wearing the design: their fields keep the padding they carry for the icons and steppers inside them, take the control radius, and need no token mapping. */
  host?: 'site' | 'portal';
  /** Whether the page's own scrollbar is styled too; a preview inside another page leaves it alone. */
  page?: boolean;
}

const FIELD_TEXT_INPUT = "input:not([type='checkbox'], [type='radio'], [type='range'], [type='color'], [type='hidden'], [type='submit'], [type='button'], [type='reset'], [type='image'], [type='file'])";
const CHEVRON_STROKE = 'transparent calc(50% - 1px), currentColor calc(50% - 1px), currentColor calc(50% + 1px), transparent calc(50% + 1px)';
const THUMB = 'var(--site-scrollbar-thumb, var(--site-scrollbar-thumb-base))';
const TRACK = 'var(--site-scrollbar-track, var(--site-scrollbar-track-base))';

function scrollbarCss(style: SiteDesign['controls']['scrollbar'], { scope, palette, page = true }: ControlsScope): string[] {
  if (style === 'browser') return [];
  const brand = style === 'brand';
  const pageThumb = `var(--site-scrollbar-thumb, ${brand ? palette.accent : `color-mix(in srgb, ${palette.text} 35%, transparent)`})`;
  const pageTrack = `var(--site-scrollbar-track, ${brand ? palette.background : 'transparent'})`;
  return [
    ...(page
      ? [
          rule('html', ['scrollbar-width: thin', `scrollbar-color: ${pageThumb} ${pageTrack}`]),
          rule('html::-webkit-scrollbar', ['width: 10px', 'height: 10px']),
          rule('html::-webkit-scrollbar-track', [`background: ${pageTrack}`]),
          rule('html::-webkit-scrollbar-thumb', [`background: ${pageThumb}`, 'border-radius: 999px', `border: 2px solid ${pageTrack}`]),
        ]
      : []),
    rule(`:is(${scope}, ${scope} [data-scheme])`, [
      `--site-scrollbar-thumb-base: ${brand ? 'var(--brand-primary)' : 'color-mix(in srgb, var(--brand-text) 35%, transparent)'}`,
      `--site-scrollbar-track-base: ${brand ? 'var(--brand-bg)' : 'transparent'}`,
      `scrollbar-color: ${THUMB} ${TRACK}`,
    ]),
    rule(`:where(${scope} *)`, ['scrollbar-width: thin']),
    rule(`:where(${scope} *)::-webkit-scrollbar`, ['width: 8px', 'height: 8px']),
    rule(`:where(${scope} *)::-webkit-scrollbar-track`, [`background: ${TRACK}`]),
    rule(`:where(${scope} *)::-webkit-scrollbar-thumb`, [`background: ${THUMB}`, 'border-radius: 999px']),
  ];
}

function fieldCss(style: SiteDesign['controls']['field'], { scope, host = 'site' }: ControlsScope): string[] {
  const portal = host === 'portal';
  const text = `${scope} :is(${FIELD_TEXT_INPUT}, select, textarea):not(.sr-only)`;
  const file = `${scope} input[type='file']`;
  const state = (pseudo: string) => [text, file].map((selector) => `${selector}${pseudo}`).join(', ');
  const edge = 'color-mix(in srgb, var(--brand-text) 30%, transparent)';
  const edgeHover = 'color-mix(in srgb, var(--brand-text) 50%, transparent)';
  const ring = 'color-mix(in srgb, var(--brand-primary) 35%, transparent)';
  const line = style === 'line';
  const look: Record<typeof style, string[]> = {
    outline: [`border: 1px solid ${edge}`, 'background-color: transparent'],
    filled: ['border: 1px solid transparent', 'background-color: color-mix(in srgb, var(--brand-text) 6%, transparent)'],
    line: ['border: 0', `border-bottom: 1px solid ${edge}`, 'border-radius: 0', 'background-color: transparent'],
  };
  const quick = 'var(--site-dur-quick, 180ms) var(--site-ease-out, ease-out)';
  return [
    rule(state(''), [
      'box-sizing: border-box',
      'color: var(--brand-text)',
      'font: inherit',
      'font-size: 1rem',
      `border-radius: ${portal ? 'var(--radius-control)' : 'var(--radius-lg)'}`,
      'outline: none',
      `transition: border-color ${quick}, box-shadow ${quick}, background-color ${quick}`,
      ...look[style],
    ]),
    rule(`${scope} :is(${FIELD_TEXT_INPUT}, select):not(.sr-only)`, ['height: 3rem', !portal && (line ? 'padding-inline: 0' : 'padding-inline: 0.875rem')]),
    rule(`${scope} textarea`, ['height: auto', 'min-height: 8rem', 'padding-block: 0.75rem', !portal && (line ? 'padding-inline: 0' : 'padding-inline: 0.875rem'), 'resize: vertical']),
    rule(file, ['height: auto', 'min-height: 3rem', 'padding: 0.5rem 0.875rem']),
    rule(`${scope} :is(${FIELD_TEXT_INPUT}, textarea)::placeholder`, ['color: color-mix(in srgb, var(--brand-text) 55%, transparent)', 'opacity: 1']),
    rule(`${file}::file-selector-button`, ['margin-inline-end: 0.75rem', 'height: 2rem', 'padding-inline: 0.875rem', 'border: 0', 'border-radius: var(--radius-md)', 'background: var(--brand-primary)', 'color: var(--brand-primary-foreground)', 'font-weight: 600', 'cursor: pointer']),
    rule(state(':hover:not(:focus, :disabled)'), [line ? `border-bottom-color: ${edgeHover}` : style === 'outline' ? `border-color: ${edgeHover}` : false]),
    rule(state(':focus'), line ? ['border-bottom-color: var(--brand-primary)', `box-shadow: 0 3px 0 0 ${ring}`] : ['border-color: var(--brand-primary)', `box-shadow: 0 0 0 3px ${ring}`]),
    rule(state(':disabled'), ['opacity: 0.55', 'cursor: not-allowed']),
    rule(`${scope} select:not([multiple])`, [
      'appearance: none',
      'padding-inline-end: 2.75rem',
      `background-image: linear-gradient(to top right, ${CHEVRON_STROKE}), linear-gradient(to top left, ${CHEVRON_STROKE})`,
      'background-repeat: no-repeat',
      'background-size: 0.5rem 0.5rem',
      'background-position: right calc(1.25rem + 0.4rem) center, right 1.25rem center',
    ]),
    rule(`${scope} :is(input[type='checkbox'], input[type='radio'], input[type='range'], progress)`, ['accent-color: var(--brand-primary)']),
  ];
}

function sharedComponentsCss({ scope }: ControlsScope): string[] {
  return [
    rule(`:is(${scope}, ${scope} [data-scheme])`, [
      '--color-white: var(--brand-bg)',
      '--color-surface: var(--brand-bg)',
      '--color-ink: var(--brand-text)',
      '--color-ink-muted: var(--brand-muted)',
      '--color-line: var(--brand-line)',
      '--color-slate-50: var(--brand-surface)',
      '--color-slate-100: var(--brand-surface-strong)',
      '--color-slate-200: var(--brand-line)',
      '--color-slate-300: color-mix(in srgb, var(--brand-text) 28%, transparent)',
      '--color-slate-400: color-mix(in srgb, var(--brand-text) 45%, transparent)',
      '--radius-control: var(--radius-lg)',
    ]),
  ];
}

/**
 * Scrollbars and form fields for a site, drawn from the design's `controls` (the defaults when it has none)
 * under whatever root `scope` names. Colours come from the `--brand-*` variables of the scheme a control sits
 * in, so a field on a dark band is a dark field; only the page's own scrollbar, which sits above every scheme,
 * takes concrete colours from `palette`. A gym's custom CSS can override `--site-scrollbar-thumb` and
 * `--site-scrollbar-track`. The sign-in pages and the member area call this with their own scope.
 */
export function siteControlsCss(design: SiteDesign | null, options: ControlsScope): string {
  const controls = design?.controls ?? { scrollbar: 'brand' as const, field: 'outline' as const };
  const shared = options.host === 'portal' ? [] : sharedComponentsCss(options);
  return [...shared, ...scrollbarCss(controls.scrollbar, options), ...fieldCss(controls.field, options)].filter(Boolean).join('\n');
}

/** Everything the site adds to its stylesheet: the design's rules, then the branded controls, then the gym's own CSS, so custom CSS has the last word. */
export function siteStyleSheet(design: SiteDesign | null, assets: Record<string, PublicAsset>, palette: ControlsPalette): string {
  return [...(design ? designRules(design, assets) : []), siteControlsCss(design, { scope: '.site', palette }), design ? customRules(design) : ''].filter(Boolean).join('\n');
}

const CONTROL_RADII: Record<Radius, string | null> = { none: '0', sm: '0.25rem', md: null, lg: '0.75rem', full: '1.5rem' };

/**
 * The part of a design the member area wears: its fonts and its corners, scoped to `.member-design`. The
 * site's case, button and scheme rules stay on the site, so Bulk's own components keep their contrast.
 */
export function memberDesignCss(design: SiteDesign | null, assets: Record<string, PublicAsset>): string {
  if (!design) return '';
  const { typography: type, shape } = design;
  const heading = fontStack(type.heading);
  const body = fontStack(type.body);
  const radii = shape.radius ? RADII[shape.radius] : {};
  const control = shape.radius ? CONTROL_RADII[shape.radius] : null;
  return [
    fontFaces(design, assets),
    rule('.member-design', [...Object.entries(radii).map(([key, value]) => (key === 'card' ? `--radius-card: ${value}` : `--radius-${key}: ${value}`)), control && `--radius-control: ${control}`, body && `font-family: ${body}`]),
    rule('.member-design :is(h1, h2, h3)', [heading && `font-family: ${heading}`]),
  ]
    .filter(Boolean)
    .join('\n');
}

/** The design's fonts and stylesheet, drawn inside the site so they go wherever the site goes. A `<link>` in the body does not hold the page back. */
export function DesignStyles({ design, assets, branding }: { design: SiteDesign | null; assets: Record<string, PublicAsset>; branding: Branding }) {
  const href = googleFontsHref(design);
  const css = siteStyleSheet(design, assets, schemeColors('default', branding, design));
  return (
    <>
      {href ? <link rel="stylesheet" href={href} /> : null}
      <style>{css}</style>
    </>
  );
}
